// Occupancy is one entry per room — adults plus an age per child. It is the
// exact shape ETG's search and booking take (Api.Hotel.Rooms.Body.guests), so
// it threads from search → rooms → booking unchanged. Encoded as JSON in the
// router params that carry it between screens.

type Occupancy = NonNullable<Api.Search.Hotels.Body['guests']>;

const DEFAULT_OCCUPANCY: Occupancy = [{ adults: 1, children: [] }];
// Search supports up to three copies of the same room type. Multi-room-type
// bookings are intentionally not represented in this selector yet.
const MAX_ROOMS = 3;
const MAX_ADULTS_PER_ROOM = 6;
const MAX_CHILDREN_PER_ROOM = 4;
const MAX_CHILD_AGE = 17;

function encodeOccupancy(occ: Occupancy): string {
    return JSON.stringify(occ);
}

function decodeOccupancy(raw: string | undefined): Occupancy {
    if (!raw) {
        return DEFAULT_OCCUPANCY;
    }

    try {
        const parsed = JSON.parse(raw) as unknown;
        if (
            Array.isArray(parsed) &&
            parsed.length > 0 &&
            parsed.every(room => room && typeof room.adults === 'number' && Array.isArray(room.children))
        ) {
            const rooms = parsed as Array<{ adults: number; children: unknown[] }>;
            return rooms.slice(0, MAX_ROOMS).map(room => ({
                adults: Math.min(MAX_ADULTS_PER_ROOM, Math.max(1, Math.round(room.adults))),
                children: room.children
                    .slice(0, MAX_CHILDREN_PER_ROOM)
                    .map(age => Math.min(MAX_CHILD_AGE, Math.max(0, Math.round(Number(age) || 0)))),
            }));
        }
    } catch {
        // malformed param — fall back to the default below
    }

    return DEFAULT_OCCUPANCY;
}

function guestCount(occ: Occupancy): number {
    return occ.reduce((sum, room) => sum + room.adults + room.children.length, 0);
}

function occupancySummary(occ: Occupancy): string {
    const rooms = occ.length;
    const guests = guestCount(occ);

    return `${rooms} ${rooms === 1 ? 'room' : 'rooms'} · ${guests} ${guests === 1 ? 'guest' : 'guests'}`;
}

export {
    DEFAULT_OCCUPANCY,
    MAX_ROOMS,
    MAX_ADULTS_PER_ROOM,
    MAX_CHILDREN_PER_ROOM,
    MAX_CHILD_AGE,
    encodeOccupancy,
    decodeOccupancy,
    guestCount,
    occupancySummary
};
export type { Occupancy };
